import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { formatAppointmentDateTime } from "../utils/dateUtils";
import { RATE_TYPE } from "../common/Constant";
import { formatFieldName } from "../utils/formatFieldName";
import { formatCurrency } from "../utils/formatCurrency";

const billingConfig = process.env.REACT_APP_PICKLIST_URL ?? "";
const billingConfigurationAPI = api(billingConfig);

interface ApiResponse<T> {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  result: T;
  timestamp: string;
}

export interface BillingStandardCharge {
  billingConfigStdChgId: number;
  standardChargeNote: string;
  standardChargeDesc: string;
  priorityId: number;
  priorityName: string;
  caseExamTypes: string;
  currency: string;
  standardChargeRate: number;
  status: string;
  lastUpdatedOn: string;
  responseCode: number;
  responseMessage: string;
  lastUpdated?: string;
  totalPages?: number;
  totalRecords?: number;
  clientRateStandardChargeRate?: number;
}

export interface BillingStdChargeChangeLog {
  billingConfigStdChgId: number;
  auditAction: string;
  fieldUpdated: string;
  oldValue: string;
  newValue: string;
  changedBy: number;
  changedByName: string;
  changedOn: string;
}

export interface BillingStandardChargesSearchPayload {
  searchString: string;
  pageNo: number;
  pageSize: number;
  sortColumn: string;
  sortOrder: string;
}

export interface AddBillingStandardChargesPayload {
  billingConfigStdChgId: number | null;
  standardChargeNote: string;
  standardChargeDesc: string;
  priorityId: number;
  caseTypeIdCsv: string;
  examTypeIdCsv: string;
  currencyType: string;
  standardChargeRate: number;
  comments: string;
  isActive: number;
}

export interface TeamsBillingSearchPayload {
  teamId: number | null;
  caseInfoId: number | null;
  searchString: string;
  startDate: string | null;
  endDate: string | null;
  pageNo: number;
  pageSize: number;
  sortColumn: string;
  sortOrder: string;
  loggedInUserId: number;
}

export interface TeamsBillingSummary {
  tenantId: number;
  countOfMasterBilling: number;
  countOfTeamBilling: number;
  countOfCaseBilling: number;
  responseCode: number;
  responseMessage: string;
}

export const getBillingStandardChargesSearchList = async (
  payload: BillingStandardChargesSearchPayload,
): Promise<BillingStandardCharge[]> => {
  try {
    const { data } = await billingConfigurationAPI.post<
      ApiResponse<BillingStandardCharge[]>
    >("/api/billing/stdcharges/search", payload);

    if (!data?.isSuccess || !Array.isArray(data.result)) {
      return [];
    }

    return data.result.map((item) => ({
      ...item,
      formattedStandardChargeRate: formatCurrency(
        item.standardChargeRate,
        item.currency,
      ),
      formattedClientRateStandardChargeRate: formatCurrency(
        item.clientRateStandardChargeRate,
        "USD",
      ),
      lastUpdated: formatAppointmentDateTime(item.lastUpdatedOn),
      actions: { Edit: "Edit" },
    }));
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getBillingStandardChargesEditDetails = async (
  billingConfigStdChgId: number,
): Promise<any> => {
  try {
    const { data } = await billingConfigurationAPI.get(
      `/api/billing/stdcharges/details?BillingConfigStdChgId=${billingConfigStdChgId}`,
    );

    return data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getBillingStdChargesChangeLog = async (
  billingStdChargeId: number,
): Promise<BillingStdChargeChangeLog[]> => {
  try {
    const response = await billingConfigurationAPI.get<
      ApiResponse<BillingStdChargeChangeLog[]>
    >(
      `/api/billing/stdcharges/changelog?billingStdChargeId=${billingStdChargeId}`,
    );
    const updatedResult = response.data?.result.map((data) => ({
      ...data,
      fieldLabelUpdated: formatFieldName(data.fieldUpdated),
      changedOn: formatAppointmentDateTime(data.changedOn),
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const addBillingStandardCharges = async (
  payload: AddBillingStandardChargesPayload,
): Promise<ApiResponse<any>> => {
  try {
    const { data } = await billingConfigurationAPI.post<ApiResponse<any>>(
      "/api/billing/stdcharges/add",
      payload,
    );

    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateBillingStandardCharges = async (
  payload: AddBillingStandardChargesPayload,
): Promise<ApiResponse<any>> => {
  try {
    const { data } = await billingConfigurationAPI.put<ApiResponse<any>>(
      "/api/billing/stdcharges/update",
      payload,
    );
    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getTeamsBillingSearchList = async (
  payload: TeamsBillingSearchPayload,
): Promise<any> => {
  try {
    const { data } = await billingConfigurationAPI.post(
      "/api/team/config/search",
      payload,
    );

    if (!data?.isSuccess || !Array.isArray(data.result)) {
      return [];
    }

    return data.result.map((item: any) => ({
      ...item,
      effectiveFrom: formatAppointmentDateTime(item.effectiveFrom),
      effectiveTo: formatAppointmentDateTime(item.effectiveTo),
      lastUpdatedOn: formatAppointmentDateTime(item.lastUpdatedOn),
      billingFor:
        item.billingType === "Role" ? item?.webRoleName : item?.billingFor,
      rateType:
        item.rateType === RATE_TYPE.FLAT
          ? "Fixed"
          : item.rateType === RATE_TYPE.PER_PAGE
            ? "Per Page"
            : item.rateType === RATE_TYPE.PER_LINE
              ? "Per Line"
              : "",

      actions: { View: "View" },
    }));
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getTeamsBillingSummary = async (
  teamId: any,
): Promise<TeamsBillingSummary | null> => {
  try {
    const { data } = await billingConfigurationAPI.get<
      ApiResponse<TeamsBillingSummary>
    >(`/api/team/config/summary?teamId=${teamId}`);

    if (!data?.isSuccess || !data.result) {
      return null;
    }

    return data.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const addTeamBilling = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await billingConfigurationAPI.post(
      "/api/team/config/add",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateTeamBilling = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await billingConfigurationAPI.put(
      "/api/team/config/update",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const fetchTeamBillingDetails = async (
  billingTeamConfigId: any,
): Promise<any> => {
  try {
    const response = await billingConfigurationAPI.get(
      `/api/team/config/details?BillingTeamConfigId=${billingTeamConfigId}`,
    );
    const data = response.data?.result;

    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getTeamBillingChangeLog = async (
  billingTeamConfigId: any,
): Promise<any> => {
  try {
    const response = await billingConfigurationAPI.get<ApiResponse<any>>(
      `/api/team/config/changelog?billingTeamConfigId=${billingTeamConfigId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      fieldLabelUpdated: formatFieldName(data.fieldName),
      changedOn: formatAppointmentDateTime(data.changedOn),
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getTeamBillingOverridenConfiguration = async (
  masterBillingConfigId: number,
): Promise<any> => {
  try {
    const response = await billingConfigurationAPI.get<ApiResponse<any>>(
      `/api/billing/config/team/overrides?masterBillingConfigId=${masterBillingConfigId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      effectiveFrom: formatAppointmentDateTime(data.effectiveStartDate),
      rateType:
        data.rateType === RATE_TYPE.FLAT
          ? "Fixed"
          : data.rateType === RATE_TYPE.PER_PAGE
            ? "Per Page"
            : data.rateType === RATE_TYPE.PER_LINE
              ? "Per Line"
              : "",
      CaseExamType: [data.caseTypeNames, data.examTypeNames]
        .filter(Boolean)
        .join(" | "),
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getCaseBillingOverridenConfiguration = async (
  overriddenBillingConfigId: number,
): Promise<any> => {
  try {
    const response = await billingConfigurationAPI.get<ApiResponse<any>>(
      `/api/billing/config/case/overrides?overriddenBillingConfigId=${overriddenBillingConfigId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      modifiedDate: formatAppointmentDateTime(data.modifiedDate),
      rateType:
        data.rateType === RATE_TYPE.FLAT
          ? "Fixed"
          : data.rateType === RATE_TYPE.PER_PAGE
            ? "Per Page"
            : data.rateType === RATE_TYPE.PER_LINE
              ? "Per Line"
              : "",
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
